import React, { useRef, useState, useMemo } from 'react';
import styles from './HATVApprovalActionModal.module.scss';
import { useTranslation } from 'react-i18next';
import dropdownArrowLight from '../../../assets/images/LightTheme/down_arrow_light.svg';
import dropdownArrowDark from '../../../assets/images/DarkTheme/down_arrow_dark.svg';
import { useAppSelector } from '../../../redux/hooks';
import { HATV_APPROVAL_ACTION } from '../../../constants/dataConstants';
import FormatterBar from '../../common/FormatterBar/FormaterBarWithLinkmodel/FormatterBar';
import Button from '../../common/Button/Button';
import useClickOutside from '../../CustomHooks/useClickOutside';
import { useUpdateApprovalStatusMutation, useBulkApproveAwardStatusMutation } from '../../../redux/services/hatvAwardApprovalAPI';
import { getFromLocalStorage, useProfileParams } from '../../../utils/common';
import { withUtag } from '../../../utils/analytics';
import { AWARD_APPROVAL_STATUS_APPROVER } from '../../../constants/dataConstants';
import i18n from '../../../i18n';

export interface IApproveModalProps {
  aboNumber: string;
  affiliateCode: string;
  aboHatvAwardApproverIdToApprove: number | null;
  aboHatvAwardApproverIdToDeny: number | null;
  approvedFor: string;
  approvalType: number;
  onClose: () => void;
  setIsStatusUpdate?: (apiStatus: string) => void;
  currentAward?: string;
  currentStatus?: number;
  currentAwardCode?: string | number | null;
  awardQualifiedMonth?: string;
  selectedAbos?: TableRowData[];
}

const HATVApprovalActionModal: React.FC<IApproveModalProps> = ({
  aboNumber,
  affiliateCode,
  aboHatvAwardApproverIdToApprove,
  aboHatvAwardApproverIdToDeny,
  approvedFor,
  approvalType,
  onClose,
  setIsStatusUpdate,
  currentAward,
  currentStatus,
  currentAwardCode,
  awardQualifiedMonth,
  selectedAbos,
}) => {
  const { t } = useTranslation();
  const fixedT = i18n.getFixedT('en');
  const isBulk = !!selectedAbos && selectedAbos.length > 0;
  
  const [isOpen, setIsOpen] = useState(false);
  const [selectedApprovalStatus, setSelectedApprovalType] = useState<number | null>(
    isBulk ? HATV_APPROVAL_ACTION.APPROVE : approvalType
  );
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const dropdownArrow = currentTheme === 'light' ? dropdownArrowLight : dropdownArrowDark;

  const [textareaValue, setTextareaValue] = useState<string>('');
  const [isEditable, setIsEditable] = useState(false);
  const [saveEnable, setSaveEnable] = useState(false);

  const [updateApprovalStatus, { isLoading: isUpdating }] = useUpdateApprovalStatusMutation();
  const [bulkApproveAwardStatus, { isLoading: isBulkUpdating }] = useBulkApproveAwardStatusMutation();

  const { aboNumber: urlParamAboNumber } = useProfileParams();
  const isDetailsPage = !!(urlParamAboNumber && urlParamAboNumber?.toString() === aboNumber);
  
  const approvalDropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside(approvalDropdownRef, () => setIsOpen(false));

  const toggleDropdown = (e: React.MouseEvent) => {
    if (isBulk) return;
    setIsOpen((isOpen) => !isOpen);
  };

  const handleApprovalChange = (selectedCode: number) => {
    setSelectedApprovalType(selectedCode);
    setIsOpen(false);
  };

  const textAreaChangeHandler = (editorData: any) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(editorData, 'text/html');
    const rowNote = doc.body.textContent || '';
    setSaveEnable(rowNote.trim().length > 0);
    editorData === '<p><br></p>' ? setTextareaValue('') : setTextareaValue(editorData);
  };

  const handleClickCancel = () => {
    onClose();
  };

  const bulkNamesText = useMemo(() => {
    if (!selectedAbos) return '';
    const names = selectedAbos.map(abo => abo.name);
    if (names.length <= 3) return names.join(', ');
    const firstThree = names.slice(0, 3).join(', ');
    const othersCount = names.length - 3;
    return `${firstThree}, ${t('and')} ${othersCount} ${t('others')}`;
  }, [selectedAbos, t]);

  const flaggedAbosText = useMemo(() => {
    if (!selectedAbos) return null;
    const flaggedNames = selectedAbos
      .filter((abo) => abo.flag?.toLowerCase() === 'red' || abo.flag?.toLowerCase() === 'yellow')
      .map((abo) => abo.name);
    
    if (flaggedNames.length === 0) return null;
    
    let namesStr = '';
    if (flaggedNames.length === 1) {
      namesStr = flaggedNames[0];
    } else {
      const last = flaggedNames.pop();
      namesStr = `${flaggedNames.join(', ')} ${t('and')} ${last}`;
    }
    
    return t('flagged_q_month_auto_update_msg', { names: namesStr });
  }, [selectedAbos, t]);

  const handleClickSave = async () => {
    if (!selectedApprovalStatus) return;

    if (isBulk) {
      const payload = {
        awards: selectedAbos!.map(abo => ({
          aboHatvAwardApproverId: abo.roleIndicators?.aboHatvAwardApproverIdToApprove || 0,
          affiliateCode: abo.affiliateCode,
          aboNumber: abo.aboNumber,
          awardCode: abo.trackingAward?.awardCode || abo.currentAward.awardCode,
          awardQualifiedMonth: abo.trackingAward?.awardQualifiedMonth || '',
        })),
        note: textareaValue,
      };

      bulkApproveAwardStatus({ payload })
        .unwrap()
        .then(() => {
          setIsStatusUpdate && setIsStatusUpdate('success');
          onClose();
        })
        .catch(() => {
          setIsStatusUpdate && setIsStatusUpdate('fail');
        });
    } else {
      const aboHatvAwardApproverId =
        selectedApprovalStatus === HATV_APPROVAL_ACTION.APPROVE
          ? aboHatvAwardApproverIdToApprove
          : aboHatvAwardApproverIdToDeny;

      if (!aboHatvAwardApproverId) return;

      updateApprovalStatus({
        aboNumber,
        affiliateCode,
        payload: {
          aboHatvAwardApproverId,
          approvalStatus: selectedApprovalStatus,
          note: textareaValue,
        },
      })
        .unwrap()
        .then(() => {
          setIsStatusUpdate && setIsStatusUpdate('success');
          onClose();
          
          const approvalCurrentStatus = AWARD_APPROVAL_STATUS_APPROVER.find(
            (statusCode) => statusCode?.code === Number(currentStatus)
          )?.label || AWARD_APPROVAL_STATUS_APPROVER[0].label;
          
          const currentAwardDetails = getFromLocalStorage<any>('currentAward');
          const awardStatusTrackData = {
            event_name: 'award_status_change',
            abo_account: aboNumber,
            current_award: isDetailsPage
              ? currentAwardDetails?.currentAwardName || ''
              : currentAward || '',
            award_status: fixedT(`HATV_APPROVAL_STATUS.${approvalCurrentStatus}`),
            to_status: selectedApprovalStatus === HATV_APPROVAL_ACTION.APPROVE ? 'Approve' : 'Deny',
            approval_type: isDetailsPage ? 'award_detail' : 'award_dashboard',
            award_id: isDetailsPage
              ? currentAwardDetails?.currentAwardCode || ''
              : currentAwardCode || '',
            award_month_qual: awardQualifiedMonth || '',
          };
          withUtag().then((utag) => utag.link(awardStatusTrackData));
        })
        .catch(() => {
          setIsStatusUpdate && setIsStatusUpdate('fail');
        });
    }
  };

  return (
    <div className={`approval_container ${styles.approval_container}`}>
      <div className={`${styles.modal_title}`}>{t('update_award_status')}</div>
      <div className={`${styles.approvedForSection}`}>
        {isBulk ? (
          <>
            <span className={styles.bold_text}>{t('Approve')}</span>
            {` ${t('award_status_for')}`}
          </>
        ) : (
          t('update_award_status_for')
        )}
        <span className={`${styles.approvedForText}`}>
          {isBulk ? ` ${bulkNamesText}` : ` ${approvedFor}`}
        </span>
      </div>

      {isBulk && flaggedAbosText && (
        <div className={styles.flagged_msg}>
          {flaggedAbosText}
        </div>
      )}
      
      {!isBulk && (
        <div
          className={`${styles.input_wrapper}`}
          ref={approvalDropdownRef}
        >
          <input
            type="text"
            className={`approval_input ${styles.dropdown_input} ${isOpen ? 'selected_input' : ''}`}
            value={selectedApprovalStatus ? t(`hatv_approval_actions.${selectedApprovalStatus}`) : ''}
            readOnly
            onClick={toggleDropdown}
            placeholder={t('award_status')}
          />
          <img
            src={dropdownArrow}
            alt="Downkey"
            className={`${styles.arrow_icon} ${isOpen ? styles.rotate : ''}`}
            onClick={toggleDropdown}
          />
          {isOpen && (
            <div className={`dropdown_menu ${styles.dropdown_menu}`}>
              {aboHatvAwardApproverIdToApprove && (
                <div
                  className={`dropdown_item ${selectedApprovalStatus === HATV_APPROVAL_ACTION.APPROVE ? 'dropdown_selected_item' : ''} ${styles.dropdown_item}`}
                  onClick={() => handleApprovalChange(HATV_APPROVAL_ACTION.APPROVE)}
                >
                  {t(`hatv_approval_actions.${HATV_APPROVAL_ACTION.APPROVE}`)}
                </div>
              )}
              {aboHatvAwardApproverIdToDeny && (
                <div
                  className={`dropdown_item ${selectedApprovalStatus === HATV_APPROVAL_ACTION.DENY ? 'dropdown_selected_item' : ''} ${styles.dropdown_item}`}
                  onClick={() => handleApprovalChange(HATV_APPROVAL_ACTION.DENY)}
                >
                  {t(`hatv_approval_actions.${HATV_APPROVAL_ACTION.DENY}`)}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className={styles.button_input_wrapper}>
        <div className={styles.formatter_wrapper}>
          <FormatterBar
            id="notes"
            editorData={textareaValue}
            handleDataChange={textAreaChangeHandler}
            section={'htvApproval'}
            placeholder={t('add_note_placeholder')}
            isContentEditable={isEditable}
            maxCharacter={1000}
          />
        </div>
        <div className={styles.btn_wrapper}>
          <Button
            id="cancelButton"
            onClick={handleClickCancel}
            label={t('cancel_btn')}
            style="unfilled"
            className={styles.btn_styles}
          />
          <Button
            id="saveButton"
            onClick={handleClickSave}
            label={t('update_btn')}
            style="filled"
            isDisabled={!selectedApprovalStatus || isUpdating || isBulkUpdating || (isBulk && !saveEnable)}
            className={styles.btn_styles}
          />
        </div>
      </div>
    </div>
  );
};

export default HATVApprovalActionModal;
